import React, { useState, useEffect } from 'react'
import { useTabContext } from '../../contexts/TabContext'
import { useAuth } from '../../contexts/AuthContext'
import { backendService, LibraryData, Project } from '../../services/backendService'
import { logUserActionError, logInfo, logError } from '../../utils/errorLogger'
import { log } from '../../utils/logManager'
import { useStabilityManager } from '../../hooks/useStabilityManager'
import { APP_VERSION } from '../../version'
import './LibraryManager.css'
import { FullscreenButton } from '../../components/fullscreen/FullscreenButton'
import { useFullscreen } from '../../hooks/useFullscreen'
import ToolboxStatus from './ToolboxStatus'
import PresetsBrowser from './PresetsBrowser'
import DeleteModal from '../../components/DeleteModal'
import PatientRegistrationModal from '../../components/PatientRegistrationModal'


interface LibraryItem {
  id: string
  name: string
  type: 'folder' | 'project' | 'file'
  children?: LibraryItem[]
  expanded?: boolean
}

interface InsoleProject {
  id: string
  name: string
  description: string
  status: 'new' | 'stl_imported' | 'processed'
  customerId: string
}

interface InsoleView {
  id: string
  name: string
  type: string
  available: boolean
  description: string
}





// Available views based on project status
const getAvailableViews = (status: InsoleProject['status']): InsoleView[] => {
  const baseViews: InsoleView[] = [
    { id: 'girasoles-view', name: 'Girasoles_view', type: 'Main Workbench', available: true, description: 'Main CAD workbench' },
    { id: 'stl-in', name: 'STL_IN', type: 'File Input', available: false, description: 'STL file input and processing' },
    { id: 'stl-out', name: 'STL_OUT', type: 'File Output', available: false, description: 'STL file output and export' }
  ]

  switch (status) {
    case 'new':
      return baseViews.map(view =>
        view.id === 'stl-in' ? { ...view, available: true } : view
      )
    case 'stl_imported':
      return baseViews.map(view =>
        view.id === 'stl-in' ? { ...view, available: true } : view
      )
    case 'processed':
      return baseViews.map(view => ({ ...view, available: true }))
    default:
      return baseViews
  }
}

export const LibraryManagerWindow: React.FC = () => {
  console.log('🔷 [LibraryManager] Component rendering')

  const tabContext = useTabContext()
  const { doctorId, doctorName, logout } = useAuth()

  // Stability manager for preventing race conditions and managing loading states
  const stability = useStabilityManager('LibraryManager', {
    debounceMs: 300,
    requestTtl: 10000,
    clickCooldown: 500,
    maxConcurrentRequests: 3
  })

  // Track component lifecycle
  React.useEffect(() => {
    console.log('🔷 [LibraryManager] Component MOUNTED')
    return () => {
      console.log('🔴 [LibraryManager] Component UNMOUNTING')
    }
  }, [])

  const [selectedLibraryItem, setSelectedLibraryItem] = useState<string | null>(null)
  const [selectedInsoleProject, setSelectedInsoleProject] = useState<string | null>(null)
  const [selectedInsoleView, setSelectedInsoleView] = useState<string | null>(null)
  const [libraryData, setLibraryData] = useState<LibraryItem[]>([])
  const [backendConnected, setBackendConnected] = useState(false)
  const [showPatientModal, setShowPatientModal] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showProjectModal, setShowProjectModal] = useState(false)
  const [newProjectName, setNewProjectName] = useState('')
  const [isCreatingProject, setIsCreatingProject] = useState(false)

  const [backendLibraryData, setBackendLibraryData] = useState<LibraryData | null>(null)

  // Current patient's insole projects
  const [currentInsoleProjects, setCurrentInsoleProjects] = useState<InsoleProject[]>([])
  // Per-project flags and resizable panel widths
  const [girasolesViewExists, setGirasolesViewExists] = useState<Record<string, boolean>>({})
  const [stlInExists, setStlInExists] = useState<Record<string, boolean>>({})
  const [libraryWidth, setLibraryWidth] = useState<number>(250) // keep default
  const [nameWidth, setNameWidth] = useState<number>(320) // moderately wider than 250
  const [dragging, setDragging] = useState<'left' | 'right' | null>(null)
  const [libraryTab, setLibraryTab] = useState<'patients' | 'presets'>('patients')


  // Get available views for selected project
  const { isFullscreen, toggleFullscreen } = useFullscreen()

  const handleLogout = () => {
    if (confirm('Are you sure you want to logout?')) {
      logout()
    }
  }

  // Helpers for resizing
  const clamp = (val: number, min: number, max: number) => Math.min(max, Math.max(min, val))
  const startDrag = (which: 'left' | 'right') => (e: React.MouseEvent) => {
    e.preventDefault()
    setDragging(which)
  }
  useEffect(() => {
    if (!dragging) return
    const onMove = (e: MouseEvent) => {
      if (dragging === 'left') {
        setLibraryWidth(prev => clamp(prev + (e.movementX || 0), 180, 500))
      } else if (dragging === 'right') {
        setNameWidth(prev => clamp(prev + (e.movementX || 0), 220, 600))
      }
    }
    const onUp = () => setDragging(null)
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
  }, [dragging])

  // Fetch STL_IN existence from backend when project selection changes
  useEffect(() => {
    if (!selectedInsoleProject) return

    const fetchStatus = async () => {
      try {
        log.stl('info', 'Fetching STL status', { projectId: selectedInsoleProject })
        const res = await backendService.getStlInStatus(selectedInsoleProject)
        log.stl('debug', 'STL status result', { projectId: selectedInsoleProject, exists: res?.exists })
        setStlInExists(prev => ({ ...prev, [selectedInsoleProject]: !!res?.exists }))
      } catch (error: any) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error'
        log.stl('error', 'STL status fetch failed', { projectId: selectedInsoleProject, error: errorMessage })
        if (!errorMessage.includes('500')) {
          setStlInExists(prev => ({ ...prev, [selectedInsoleProject]: false }))
        }
      }
    }

    fetchStatus()
  }, [selectedInsoleProject])

  const selectedProject = currentInsoleProjects.find((p: InsoleProject) => p.id === selectedInsoleProject)
  const availableViews = selectedProject ? getAvailableViews(selectedProject.status) : []

  // Load library data from backend on component mount
  useEffect(() => {
    log.data('debug', 'LibraryManager mounted', { doctorId })
    const initialConnectionStatus = backendService.getConnectionStatus()
    setBackendConnected(initialConnectionStatus)

    loadLibraryData()

    const unsubscribe = backendService.addConnectionListener((connected) => {
      log.data('debug', 'Connection status changed', { connected })
      setBackendConnected(connected)
      if (connected) {
        loadLibraryData()
      }
    })

    const unsubscribeMessages = backendService.addMessageListener((message) => {
      log.ws('debug', 'Backend message', { type: message.type, message: message.message })
      const t = (message.type || '').toLowerCase()
      if (
        t.includes('patient') ||
        t.includes('project') ||
        t === 'library_updated'
      ) {
        loadLibraryData()
      }
    })

    return () => {
      unsubscribe()
      unsubscribeMessages()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const loadLibraryData = async () => {
    if (!doctorId) {
      log.data('error', 'No doctor ID for library load')
      return
    }

    setSyncing(true)
    try {
      log.data('info', 'Loading library', { doctorId })
      const backendData = await backendService.getLibraryData()

      const convertedData = convertBackendToFrontendData(backendData)
      setLibraryData(convertedData)
      setBackendLibraryData(backendData)
      if (selectedLibraryItem) {
        setCurrentInsoleProjects(computeInsoleProjectsFromBackend(selectedLibraryItem, backendData))
      }
      const patients = backendData.doctors[0]?.patients || []
      log.data('info', 'Library loaded', { patients: patients.length })
      log.data('debug', 'Converted UI tree', {
        folders: convertedData.length,
        children: convertedData[0]?.children?.length ?? 0,
        childNames: convertedData[0]?.children?.map(c => c.name).join(', ') ?? 'none',
      })
    } catch (error) {
      log.data('error', 'Library load failed', { error: String(error) })
    } finally {
      setSyncing(false)
      setLastSyncedAt(new Date().toLocaleTimeString())
    }
  }

  const convertBackendToFrontendData = (backendData: LibraryData): LibraryItem[] => {
    const myDoctor = backendData.doctors.find(d => d.id === doctorId) || backendData.doctors[0]
    log.data('debug', 'Converting backend data', {
      lookupDoctorId: doctorId,
      foundDoctorId: myDoctor?.id ?? 'none',
      match: myDoctor?.id === doctorId ? 'exact' : 'fallback',
    })
    const patientsFolder: LibraryItem = {
      id: 'patients',
      name: 'Patients',
      type: 'folder',
      expanded: true,
      children: myDoctor ? myDoctor.patients.map(patient => ({
        id: patient.id,
        name: patient.name,
        type: 'folder' as const,
        children: []
      })) : []
    }
    return [patientsFolder]
  }

  const mapProjectToInsoleProject = (p: Project): InsoleProject => ({
    id: p.id,
    name: p.name,
    description: p.description,
    status: p.status === 'stl_imported' ? 'stl_imported' : p.status === 'processed' ? 'processed' : 'new',
    customerId: p.patientId,
  })

  const computeInsoleProjectsFromBackend = (patientId: string, data: LibraryData): InsoleProject[] => {
    const doc = data.doctors.find(d => d.id === doctorId) || data.doctors[0]
    const patient = doc?.patients.find(p => p.id === patientId)
    return (patient?.projects || []).map(mapProjectToInsoleProject)
  }

  const toggleFolder = (itemId: string) => {
    const updateItem = (items: LibraryItem[]): LibraryItem[] => {
      return items.map(item => {
        if (item.id === itemId) {
          return { ...item, expanded: !item.expanded }
        }
        if (item.children) {
          return { ...item, children: updateItem(item.children) }
        }
        return item
      })
    }
    setLibraryData(updateItem(libraryData))
  }

  // Real-time sync: periodic polling
  // Note: WebSocket connection is managed by AuthContext, not here
  useEffect(() => {
    if (!doctorId) return
    const intervalId = setInterval(() => {
      loadLibraryData()
    }, 15000)
    return () => {
      clearInterval(intervalId)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorId])

  const handleLibraryItemClick = (item: LibraryItem, level: number) => {
    console.log(`LibraryManager: Clicked item "${item.name}" (ID: ${item.id}) at level ${level}`)

    if (item.type === 'folder' && item.children && item.children.length > 0) {
      // If it's a folder with children, toggle expansion
      toggleFolder(item.id)
    }

    // If it's a patient (level 1), select it for project operations
    if (level === 1) {
      console.log(`LibraryManager: Selecting patient "${item.name}" (ID: ${item.id})`)
      setSelectedLibraryItem(item.id)
      setSelectedInsoleProject(null) // Clear project selection when changing patient
      setSelectedInsoleView(null) // Clear view selection
      // Populate current patient's insole projects
      if (backendLibraryData) {
        setCurrentInsoleProjects(computeInsoleProjectsFromBackend(item.id, backendLibraryData))
      } else {
        setCurrentInsoleProjects([])
      }
      logInfo(`Patient selected: ${item.name}`, 'Library Manager')
    }
  }

  const renderLibraryTree = (items: LibraryItem[], level: number = 0): React.ReactNode => {
    return items.map(item => {
      const isPatient = level === 1 // Patients are at level 1 under the Patients folder
      const isSelectable = isPatient

      // Reduced logging: Debug logging for level detection
      // if (isPatient) {
      //   console.log(`LibraryManager: Rendering patient "${item.name}" at level ${level}`)
      // }

      const isSelected = isPatient && selectedLibraryItem === item.id

      return (
        <div key={item.id} className="library-item">
          <div
            className={`library-item-content ${
              isSelected ? 'selected' : ''
            } ${isSelectable ? 'selectable' : ''} ${isPatient ? 'patient-item' : ''}`}
            style={{ paddingLeft: `${level * 16 + 8}px` }}
            onClick={() => handleLibraryItemClick(item, level)}
          >
            <span className="library-item-icon">
              {item.type === 'folder' ? (item.expanded ? '📂' : '📁') : '📄'}
            </span>
            <span className="library-item-name">{item.name}</span>
            {isPatient && selectedLibraryItem === item.id && (
              <span className="selection-indicator">👤</span>
            )}
          </div>
          {item.expanded && item.children && (
            <div className="library-item-children">
              {renderLibraryTree(item.children, level + 1)}
            </div>
          )}
        </div>
      )
    })
  }

  const handleOpenView = (viewId: string) => {
    return stability.safeClick(
      `open-view-${viewId}`,
      () => {
        if (!selectedProject) {
          logUserActionError('Open View', 'Library Manager', 'No project selected')
          return
        }

        const customerName = (libraryData.find(i => i.id === 'patients')?.children || [])
          .find(customer => customer.id === selectedLibraryItem)?.name || 'Unknown Patient'

        logInfo(`Opening ${viewId} view for project "${selectedProject.name}"`, 'Library Manager')

        switch (viewId) {
          case 'girasoles-view':
            tabContext.addGirasolesView(undefined, selectedProject.id)
            logInfo(`Created new Girasoles View tab for project "${selectedProject.name}" (ID: ${selectedProject.id})`, 'Library Manager')
            break
          case 'stl-in':
            console.log(`LibraryManager: Opening STL_IN for project:`, {
              projectName: selectedProject.name,
              projectId: selectedProject.id,
              customerName: customerName
            })
            tabContext.addSTLInView(selectedProject.name, customerName, selectedProject.id)
            logInfo(`Created STL_IN tab for project "${selectedProject.name}"`, 'Library Manager')
            break
          case 'stl-out':
            tabContext.addSTLOutView(selectedProject.name, customerName, selectedProject.id)
            logInfo(`Created STL_OUT tab for project "${selectedProject.name}"`, 'Library Manager')
            break
          default:
            logUserActionError('Open View', 'Library Manager', `Unknown view type: ${viewId}`)
            alert(`Opening ${viewId} view for "${selectedProject.name}"`)
        }
      },
      { cooldown: 1000 }
    )
  }

  const getSelectedPatientName = () => (libraryData.find(i => i.id === 'patients')?.children || [])
    .find(patient => patient.id === selectedLibraryItem)?.name || 'Unknown Patient'

  const closeProjectModal = () => {
    if (isCreatingProject) return
    setShowProjectModal(false)
    setNewProjectName('')
  }

  const createInsoleProject = async (projectName: string) => {
    if (!doctorId) {
      alert('Not authenticated. Please log in again.')
      return
    }

    if (!selectedLibraryItem) {
      alert('Please select a patient first before creating a project')
      return
    }

    const trimmedProjectName = projectName.trim()
    if (!trimmedProjectName) {
      return
    }

    const patientName = getSelectedPatientName()
    const doctorLabel = doctorName || 'Unknown Doctor'

    setIsCreatingProject(true)
    try {
      const resp = await backendService.createProject({
        name: trimmedProjectName,
        patientId: selectedLibraryItem,
        doctorId,
        type: 'insole_design',
        description: ''
      })

      // Normalize backend response to Project shape (handles projectId vs id)
      const newId = (resp as any).projectId ?? (resp as any).id
      const normalizedProject: Project = {
        id: newId,
        name: (resp as any).name ?? trimmedProjectName,
        status: (resp as any).status ?? 'new',
        description: (resp as any).description ?? '',
        createdDate: (resp as any).createdDate ?? new Date().toISOString(),
        patientId: selectedLibraryItem,
        doctorId,
        type: 'insole_design'
      }

      // Update local state list immediately for responsive UI
      const mapped: InsoleProject = mapProjectToInsoleProject(normalizedProject)
      setCurrentInsoleProjects(prev => [...prev, mapped])
      setSelectedInsoleProject(normalizedProject.id)

      // Update cached backend library data so selections remain consistent
      setBackendLibraryData(prev => {
        if (!prev) return prev
        return {
          ...prev,
          doctors: prev.doctors.map(d =>
            (d.id === doctorId ? {
              ...d,
              patients: d.patients.map(p =>
                (p.id === selectedLibraryItem ? { ...p, projects: [...p.projects, normalizedProject] } : p)
              )
            } : d)
          )
        }
      })

      setShowProjectModal(false)
      setNewProjectName('')
      logInfo(`New insole project created: ${normalizedProject.name} (ID: ${normalizedProject.id})`, 'Library Manager')
      alert(`New insole project "${trimmedProjectName}" created for ${patientName} (${doctorLabel})`)
    } catch (error) {
      logError(`Failed to create insole project: ${String(error)}`, 'Library Manager')
      alert('Failed to create insole project. Please try again.')
    } finally {
      setIsCreatingProject(false)
    }
  }

  const handleNewInsoleProject = () => {
    if (!doctorId) {
      alert('Not authenticated. Please log in again.')
      return
    }

    if (!selectedLibraryItem) {
      alert('Please select a patient first before creating a project')
      return
    }

    setNewProjectName('')
    setShowProjectModal(true)
  }

  const handleNewInsoleView = (viewType: 'girasoles_view' | 'stl_in') => {
    if (!selectedInsoleProject) {
      logUserActionError('New View', 'Library Manager', 'No insole project selected')
      alert('Please select an insole project first')
      return
    }

    try {
      const customerName = (libraryData.find(i => i.id === 'patients')?.children || [])
        .find(customer => customer.id === selectedLibraryItem)?.name || 'Unknown Patient'

      switch (viewType) {
        case 'girasoles_view':
          tabContext.addGirasolesView(undefined, selectedProject?.id)
          if (selectedInsoleProject) {
            setGirasolesViewExists(prev => ({ ...prev, [selectedInsoleProject]: true }))
          }
          logInfo(`Created new Girasoles View for project "${selectedProject?.name}" (ID: ${selectedProject?.id})`, 'Library Manager')
          break
        case 'stl_in':
          if (selectedProject) {
            tabContext.addSTLInView(selectedProject.name, customerName, selectedProject.id)
            logInfo(`Created new STL_IN view for project "${selectedProject.name}"`, 'Library Manager')
          }
          break
      }
    } catch (error) {
      logError(`Failed to create new ${viewType} view: ${error}`, 'Library Manager')
    }
  }

  // Menu handlers
  const handleMenuAction = (action: string) => {
    switch (action) {
      case 'add-patient':
        if (!doctorId) {
          alert('Not authenticated. Please log in again.')
          return
        }
        setShowPatientModal(true)
        break
      case 'contact-support':
        alert('Contact Support: support@gira-soles.com\n\nPlease use this email for reporting issues and technical support.')
        break
      case 'visit-website':
        if (confirm('Open Gira-Soles.com in your browser?')) {
          window.open('https://gira-soles.com', '_blank', 'noopener,noreferrer')
        }
        break
      case 'open-marketplace':
        tabContext.addMarketplace()
        break
      case 'open-ai-assistant':
        tabContext.addMarketplace({ initialTab: 'girasoles-assistant' })
        break
      case 'logout':
        if (confirm('Are you sure you want to logout?')) {
          logout()
          logInfo('User logged out from Library Manager', 'Library Manager')
        }
        break
      default:
        alert(`Menu action: ${action}`)
    }
  }



  return (
    <div className="library-manager-window">
      {/* Patient Registration Modal */}
      <PatientRegistrationModal
        show={showPatientModal}
        onClose={() => setShowPatientModal(false)}
        doctorId={doctorId ?? undefined}
        doctorName={doctorName ?? undefined}
        onPatientAdded={loadLibraryData}
      />

      {/* Project Creation Modal */}
      {showProjectModal && (
        <div className="modal-overlay" onClick={closeProjectModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Create New Insole Project</h3>
              <button className="modal-close" onClick={closeProjectModal} disabled={isCreatingProject}>×</button>
            </div>
            <form
              onSubmit={e => {
                e.preventDefault()
                void createInsoleProject(newProjectName)
              }}
            >
              <div className="modal-body">
                <div className="form-group">
                  <label>Patient</label>
                  <div className="selected-doctor">{getSelectedPatientName()}</div>
                </div>
                <div className="form-group">
                  <label>Doctor</label>
                  <div className="selected-doctor">{doctorName || 'Unknown Doctor'}</div>
                </div>
                <div className="form-group">
                  <label htmlFor="projectName">Project Name</label>
                  <input
                    id="projectName"
                    type="text"
                    value={newProjectName}
                    onChange={e => setNewProjectName(e.target.value)}
                    placeholder={`Enter name for new insole project for ${getSelectedPatientName()}`}
                    autoFocus
                    required
                    disabled={isCreatingProject}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-cancel" onClick={closeProjectModal} disabled={isCreatingProject}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={isCreatingProject || !newProjectName.trim()}>
                  {isCreatingProject ? 'Creating…' : 'Create Project'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Menu Bar */}
      <div className="library-menu-bar">
      {/* Delete Modal */}
      <DeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        backendLibraryData={backendLibraryData}
        doctorId={doctorId}
        selectedLibraryItem={selectedLibraryItem}
        selectedInsoleProject={selectedInsoleProject}
        girasolesViewExists={girasolesViewExists}
        onViewDeleted={(projectId, viewKey) => {
          if (selectedInsoleProject === projectId && selectedInsoleView === viewKey) {
            setSelectedInsoleView(null)
          }
        }}
        onRefresh={loadLibraryData}
      />

        <div className="menu-items">
          <div className="menu-dropdown">
            <span className="menu-item">File</span>
            <div className="dropdown-content">

              <div className="dropdown-item" onClick={() => handleMenuAction('add-patient')}>
                Add New Patient
              </div>

              <div className="dropdown-item" onClick={() => setShowDeleteModal(true)}>
                Delete...
              </div>

            </div>
          </div>
          <div className="menu-dropdown">
            <span className="menu-item">View</span>
            <div className="dropdown-content">
              <div className="dropdown-item" onClick={() => handleMenuAction('open-marketplace')}>
                Marketplace
              </div>
              <div className="dropdown-item" onClick={() => handleMenuAction('open-ai-assistant')}>
                AI Assistant
              </div>
              <div className="dropdown-separator"></div>
              <div className="dropdown-item" onClick={() => handleMenuAction('visit-website')}>
                Visit Gira-Soles.com
              </div>
            </div>
          </div>
          <div className="menu-dropdown">
            <span className="menu-item">Help</span>
            <div className="dropdown-content">
              <div className="dropdown-item" onClick={() => handleMenuAction('contact-support')}>
                Contact Support
              </div>
              <div className="dropdown-separator"></div>
              <div className="dropdown-item logout-item" onClick={() => handleMenuAction('logout')}>


                Logout
              </div>
            </div>
          </div>
        </div>
        <div className="menu-right">
          <div className="connection-status">
            <span>{backendConnected ? '● Connected' : '○ Disconnected'}</span>
            <span style={{ marginLeft: 8 }}>{syncing ? 'Syncing…' : (lastSyncedAt ? `Last sync: ${lastSyncedAt}` : 'No sync yet')}</span>
          </div>
          <div className="menu-actions">
            <ToolboxStatus />
          </div>
        </div>

      </div>

      {/* Header row with title and fullscreen */}
      <div className="library-header" role="banner">
        <div className="header-left">
          <h2 className="library-title">Library Manager</h2>
        </div>
        <div className="header-right">
          <button className="profile-btn" onClick={() => tabContext.addProfileView()} aria-label="Profile">Profile</button>
          <FullscreenButton isFullscreen={isFullscreen} onToggle={toggleFullscreen} />
          <button className="logout-btn" onClick={handleLogout} aria-label="Logout">Logout</button>
        </div>
      </div>


      {/* Three-Panel Layout */}
      <div className="library-content">
        {/* Library Panel */}
        <div className="library-panel" style={{ width: `${libraryWidth}px` }}>
          <div className="vertical-resize-handle right" onMouseDown={startDrag('left')} />
          <div className="panel-header">Library</div>
          {/* Patients / Presets tab switcher */}
          <div className="library-tab-switcher">
            <button
              className={`library-tab-btn ${libraryTab === 'patients' ? 'active' : ''}`}
              onClick={() => setLibraryTab('patients')}
              type="button"
            >
              Patients
            </button>
            <button
              className={`library-tab-btn ${libraryTab === 'presets' ? 'active' : ''}`}
              onClick={() => setLibraryTab('presets')}
              type="button"
            >
              Presets
            </button>
          </div>
          {libraryTab === 'patients' ? (
            <div className="library-tree">
              {renderLibraryTree(libraryData)}
            </div>
          ) : (
            <PresetsBrowser />
          )}
        </div>

        {/* Insole Name Panel */}
        <div className="insole-name-panel" style={{ width: `${nameWidth}px` }}>
          <div className="vertical-resize-handle right" onMouseDown={startDrag('right')} />
          <div className="panel-header">
            <span>Insole Name</span>
            <button
              className={`panel-new-btn ${(!doctorId || !selectedLibraryItem) ? 'disabled' : ''}`}
              onClick={handleNewInsoleProject}
              disabled={!doctorId || !selectedLibraryItem}
              title={
                !doctorId ? "Not authenticated" :
                !selectedLibraryItem ? "Please select a patient first" :
                "Create new insole project"
              }
            >
              New
            </button>
          </div>
          <div className="insole-list">
            {selectedLibraryItem ? (
              currentInsoleProjects.length > 0 ? (
                currentInsoleProjects.map(project => (
                  <div
                    key={project.id}
                    className={`insole-item ${selectedInsoleProject === project.id ? 'selected' : ''}`}
                    onClick={() => setSelectedInsoleProject(project.id)}
                  >
                    <div className="insole-name">{project.name}</div>
                    <div className="insole-description">{project.description}</div>
                    <div className={`insole-status status-${project.status}`}>
                      {project.status === 'new' && '🆕 New'}
                      {project.status === 'stl_imported' && '📁 STL Imported'}
                      {project.status === 'processed' && '✅ Processed'}
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-projects">
                  <p>No insole projects found for this customer.</p>
                  <button className="create-project-btn" onClick={handleNewInsoleProject} disabled={!doctorId || !selectedLibraryItem} title={!doctorId ? 'Not authenticated' : (!selectedLibraryItem ? 'Please select a patient first' : 'Create new insole project')}>+ Create New Project</button>
                </div>
              )
            ) : (
              <div className="no-selection">
                <p>Select a customer to view their insole projects</p>
              </div>
            )}
          </div>
        </div>

        {/* Insole View Panel */}
        <div className="insole-view-panel">
          <div className="panel-header">Insole View</div>
          <div className="view-list">
            {selectedInsoleProject ? (
              availableViews.map(view => (
                <div
                  key={view.id}
                  className={`view-item ${selectedInsoleView === view.id ? 'selected' : ''} ${!view.available ? 'disabled' : ''}`}
                  onClick={() => view.available && setSelectedInsoleView(view.id)}
                >
                  <div className="view-header">
                    <div className="view-name">{view.name}</div>
                    <div className="view-buttons">
                      {/* Direct action buttons per view type */}
                      {view.id === 'girasoles-view' && selectedInsoleProject && !girasolesViewExists[selectedInsoleProject] && (
                        <button
                          className={"panel-new-btn"}
                          title="Create a new Girasoles_view"
                          onClick={(e) => { e.stopPropagation(); handleNewInsoleView('girasoles_view') }}
                        >
                          New
                        </button>
                      )}
                      {view.id === 'stl-in' && (
                        <button
                          className={`panel-new-btn ${!selectedInsoleProject ? 'disabled' : ''} ${selectedInsoleProject && stlInExists[selectedInsoleProject] ? 'secondary' : ''}`}
                          disabled={!selectedInsoleProject}
                          title={(selectedInsoleProject && stlInExists[selectedInsoleProject]) ? 'Open STL_IN view to update the existing STL file' : 'Import STL file(s)'}
                          onClick={(e) => { e.stopPropagation(); if (selectedInsoleProject) handleNewInsoleView('stl_in') }}
                        >
                          {(selectedInsoleProject && stlInExists[selectedInsoleProject]) ? 'Update' : 'Import'}
                        </button>
                      )}
                      {/* No creation button for STL_OUT */}
                      <button
                        className={`open-btn ${!view.available ? 'disabled' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation()
                          if (view.available) {
                            handleOpenView(view.id)()
                          }
                        }}
                        disabled={!view.available}
                      >
                        Open
                      </button>
                    </div>
                  </div>
                  <div className="view-description">{view.description}</div>
                  {!view.available && (
                    <div className="view-unavailable">
                      {view.id === 'stl-in' && 'Available after STL import'}
                      {view.id === 'stl-out' && 'Available after processing'}
                    </div>
                  )}
                </div>
              ))
            ) : (
              <div className="no-project-selection">
                <p>Select an insole project to view available views</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Version Display */}
      <div style={{
        position: 'absolute',
        bottom: '10px',
        right: '10px',
        fontSize: '11px',
        color: '#999',
        fontFamily: 'monospace',
        userSelect: 'none',
        pointerEvents: 'none'
      }}>
        v{APP_VERSION}
      </div>

    </div>
  )
}
